import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  consumeLastVisited,
  hasLastVisited,
} from '../../components/ListV2/components/table/lastVisitedFromList';
import useInfiniteDataQuery from '../useInfiniteDataQuery/useInfiniteDataQuery';
import {
  InfiniteDataQueryCache,
  UseInfiniteDataQueryParams,
} from '../useInfiniteDataQuery/useInfiniteDataQuery.types';
import { useIsReturningFromChildRoute } from '../useIsReturningFromChildRoute';
import { findPageIndexByItemId, refetchSinglePage } from './useInfiniteListWithRestore.utils';

type Params<PageData> = Omit<UseInfiniteDataQueryParams<PageData>, 'refetchOnMount' | 'maxPages'>;

export const useInfiniteListWithRestore = <PageData>(params: Params<PageData>) => {
  const isReturningFromChildRoute = useIsReturningFromChildRoute();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const isReturningFromVisitedItem = isReturningFromChildRoute && hasLastVisited(pathname);

  const { mutateMethods, query } = useInfiniteDataQuery<PageData>({
    ...params,
    refetchOnMount: !isReturningFromVisitedItem,
    maxPages: null,
  });

  const { isLoading, queryKey } = query;
  const { limit, defaultOrderBy, queryFn } = params;

  useEffect(() => {
    if (!isReturningFromVisitedItem || isLoading) {
      return;
    }
    const visitedId = consumeLastVisited(pathname);
    if (!visitedId) {
      return;
    }
    const cache = queryClient.getQueryData<InfiniteDataQueryCache<PageData>>(queryKey);
    const pageIndex = findPageIndexByItemId(cache, visitedId);
    if (pageIndex == null) {
      return;
    }
    void refetchSinglePage({
      queryClient,
      queryKey,
      pageIndex,
      limit,
      defaultOrderBy,
      queryFn,
    });
  }, [
    isReturningFromVisitedItem,
    isLoading,
    pathname,
    queryClient,
    queryKey,
    limit,
    defaultOrderBy,
    queryFn,
  ]);

  return {
    query,
    mutateMethods,
    restoreScrollOnMount: true,
  };
};
